import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../api/client';
import './StaffPermissions.css';
import logger from '../../utils/logger';

interface TenantSettings {
    staffCanViewAnalytics: boolean;
    staffCanViewCashflow: boolean;
    staffCanManageMenu: boolean;
    staffCanViewDocuments: boolean;
    staffCanManageEvents: boolean;
    staffCanViewOrders: boolean;
    staffCanManageLocations: boolean;
    staffCanViewBranding: boolean;
    staffCanDeleteOrders: boolean;
    staffCanManagePrintCenter: boolean;
    staffCanManageUsers: boolean;
    staffCanViewPosStatistics: boolean;
    staffCanManageCashSessions: boolean;
    coverCharge: number; // 🍽️ Coperto per persona
}

// Tipo per i permessi booleani (escluso coverCharge)
type PermissionKey = Exclude<keyof TenantSettings, 'coverCharge'>;
type PermissionCategory =
    | 'operations'
    | 'pos'
    | 'finance'
    | 'analytics'
    | 'menu'
    | 'documents'
    | 'branding'
    | 'events'
    | 'locations'
    | 'admin';

type PermissionDefinition = {
    key: PermissionKey;
    label: string;
    description: string;
    category: PermissionCategory;
    modules?: string[];
};

const CATEGORY_METADATA: Record<PermissionCategory, { title: string; description?: string }> = {
    operations: {
        title: '🚀 Operatività giornaliera',
        description: 'Controlla cosa compare in Delivery & Asporto, Ordini Delivery e Centro Stampa.',
    },
    pos: {
        title: '💶 POS Touch',
        description: 'Permessi extra rispetto alla vista base dello Staff Touch POS.',
    },
    finance: {
        title: '💼 Cassa & Finanza',
        description: 'Abilita la sezione Cassa con movimenti e report economici.',
    },
    analytics: {
        title: '📊 Analytics',
        description: 'Widget e dashboard avanzate nella home del portale.',
    },
    menu: {
        title: '🍽️ Menu & Catalogo',
        description: 'Gestione menu, traduzioni e editor legacy.',
    },
    documents: {
        title: '📂 Documenti & Risorse',
        description: 'Accesso a Gestione Documenti e all’area Upload.',
    },
    branding: {
        title: '🎨 Branding',
        description: 'Modifica colori, logo e materiale marketing.',
    },
    events: {
        title: '🎟️ Eventi & Promo',
        description: 'Creazione e gestione degli eventi nel portale.',
    },
    locations: {
        title: '🏢 Locali & Location',
        description: 'Gestione sedi, sale e informazioni operative.',
    },
    admin: {
        title: '🛠️ Impostazioni & Staff',
        description: 'Gestione utenti interni e impostazioni avanzate dell’app.',
    },
};

const CATEGORY_ORDER: PermissionCategory[] = [
    'operations',
    'pos',
    'finance',
    'analytics',
    'menu',
    'documents',
    'branding',
    'events',
    'locations',
    'admin',
];

const PERMISSIONS: PermissionDefinition[] = [
    {
        key: 'staffCanViewOrders',
        label: 'Accesso Delivery & Asporto',
        description: 'Lo staff può aprire la lavagna Delivery & Asporto e la lista Ordini Delivery.',
        category: 'operations',
        modules: ['Delivery & Asporto', 'Ordini Delivery'],
    },
    {
        key: 'staffCanDeleteOrders',
        label: 'Eliminazione ordini',
        description: 'Consente di cancellare ordini delivery dalla lista Ordini Delivery.',
        category: 'operations',
        modules: ['Ordini Delivery'],
    },
    {
        key: 'staffCanManagePrintCenter',
        label: 'Centro Stampa',
        description: 'Permette di accedere al Centro Stampa e modificare stampanti e template.',
        category: 'operations',
        modules: ['Centro Stampa'],
    },
    {
        key: 'staffCanManageCashSessions',
        label: 'Gestisci sessioni di cassa',
        description: 'Autorizza l’apertura/chiusura della cassa dal POS Touch.',
        category: 'pos',
        modules: ['POS Touch – gestione cassa'],
    },
    {
        key: 'staffCanViewPosStatistics',
        label: 'Statistiche POS Touch',
        description: 'Sblocca grafici e report avanzati nella dashboard POS.',
        category: 'pos',
        modules: ['POS Touch – statistiche'],
    },
    {
        key: 'staffCanViewCashflow',
        label: 'Pagina Cassa',
        description: 'Abilita la voce "Cassa" per consultare incassi e movimenti.',
        category: 'finance',
        modules: ['Cassa'],
    },
    {
        key: 'staffCanViewAnalytics',
        label: 'Analytics dashboard',
        description: 'Consente di vedere i widget e i grafici avanzati nella home.',
        category: 'analytics',
        modules: ['Dashboard – Analytics'],
    },
    {
        key: 'staffCanManageMenu',
        label: 'Gestione menu e traduzioni',
        description: 'Permette di modificare menù, categorie, traduzioni e usare l’editor legacy.',
        category: 'menu',
        modules: ['Menu Management', 'Traduzione Menu', 'Menu Editor (legacy)'],
    },
    {
        key: 'staffCanViewDocuments',
        label: 'Documenti e Upload',
        description: 'Sblocca Gestione Documenti e l’area Upload Documenti.',
        category: 'documents',
        modules: ['Gestione Documenti', 'Upload Documenti'],
    },
    {
        key: 'staffCanViewBranding',
        label: 'Branding',
        description: 'Accesso alla pagina Branding per loghi, colori e grafiche.',
        category: 'branding',
        modules: ['Branding'],
    },
    {
        key: 'staffCanManageEvents',
        label: 'Gestione eventi',
        description: 'Permette di creare e modificare eventi e promozioni.',
        category: 'events',
        modules: ['Gestione Eventi'],
    },
    {
        key: 'staffCanManageLocations',
        label: 'Gestione locali',
        description: 'Consente di modificare sedi, sale e informazioni operative.',
        category: 'locations',
        modules: ['Gestione Locali'],
    },
    {
        key: 'staffCanManageUsers',
        label: 'Gestione staff e impostazioni app',
        description: 'Sblocca Gestione Staff, impostazioni app e configurazione permessi.',
        category: 'admin',
        modules: ['Gestione Staff', 'Impostazioni App', 'Permessi Staff'],
    },
];

export const StaffPermissions: React.FC = () => {
    const [settings, setSettings] = useState<TenantSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            logger.debug('StaffPermissions: loading settings');
            const tenantId = localStorage.getItem('tenantId');
            logger.debug('StaffPermissions: tenantId present?', Boolean(tenantId));

            const response = await apiFetch('/api/tenant-settings');

            if (response.ok) {
                const data = await response.json();
                logger.debug('StaffPermissions: settings loaded');
                setSettings(data);
            } else {
                logger.error('StaffPermissions: failed to load settings', response.status);
            }
        } catch (error) {
            logger.error('StaffPermissions: error loading settings', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = (key: PermissionKey) => {
        if (!settings) return;
        setSettings({ ...settings, [key]: !settings[key] });
    };

    const handleSave = async () => {
        if (!settings) return;

        setSaving(true);
        try {
            logger.debug('StaffPermissions: saving settings');

            const permissionKeys = Array.from(new Set(PERMISSIONS.map(perm => perm.key)));
            const payload: Record<string, boolean | number> = {};
            permissionKeys.forEach((key) => {
                payload[key] = Boolean(settings[key]);
            });
            const coverChargeValue = Number(settings.coverCharge);
            payload.coverCharge = Number.isFinite(coverChargeValue) && coverChargeValue >= 0 ? coverChargeValue : 0;

            const response = await apiFetch('/api/tenant-settings', {
                method: 'PUT',
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                logger.debug('StaffPermissions: settings saved');
                alert('Impostazioni salvate con successo!');
            } else {
                logger.error('StaffPermissions: failed to save settings', response.status);
                alert('Errore durante il salvataggio');
            }
        } catch (error) {
            logger.error('StaffPermissions: error saving settings', error);
            alert('Errore durante il salvataggio');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="staff-permissions-container">Caricamento...</div>;
    }

    if (!settings) {
        return <div className="staff-permissions-container">Errore nel caricamento delle impostazioni</div>;
    }

    const sections = CATEGORY_ORDER.map((category) => {
        const items = PERMISSIONS.filter(perm => perm.category === category);
        return items.length
            ? { category, meta: CATEGORY_METADATA[category], permissions: items }
            : null;
    }).filter((section): section is { category: PermissionCategory; meta: { title: string; description?: string }; permissions: PermissionDefinition[] } => section !== null);

    return (
        <div className="staff-permissions-container">
            <div className="permissions-header">
                <div>
                    <h1>Permessi Staff</h1>
                    <p className="permissions-subtitle">
                        Configura cosa possono fare gli utenti con ruolo Staff nel portale
                    </p>
                </div>
                <div className="permissions-actions">
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => navigate('/staff')}
                    >
                        👥 Gestisci Staff
                    </button>
                    <button
                        className="btn-save"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Salvataggio...' : '💾 Salva Modifiche'}
                    </button>
                </div>
            </div>

            <div className="permissions-notice">
                <div className="notice-icon">ℹ️</div>
                <div>
                    <strong>Nota:</strong> Gli amministratori (TENANT_ADMIN) hanno sempre accesso completo a tutte le funzionalità.
                    Queste impostazioni si applicano solo agli utenti con ruolo Staff (ROLE_USER).
                </div>
            </div>

            {/* 🍽️ SEZIONE COPERTO */}
            <div className="permissions-category">
                <h2 className="category-title">⚙️ Configurazione POS</h2>
                <div className="permissions-grid">
                    <div className="permission-card">
                        <div className="permission-info">
                            <label className="permission-label">
                                🍽️ Coperto per Persona
                            </label>
                            <p className="permission-description">
                                Importo coperto aggiunto automaticamente per ogni persona (es: 2.50 €)
                            </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>€</span>
                            <input
                                type="number"
                                step="0.50"
                                min="0"
                                max="20"
                                value={settings.coverCharge || 0}
                                onChange={(e) => setSettings({
                                    ...settings,
                                    coverCharge: parseFloat(e.target.value) || 0
                                })}
                                style={{
                                    width: '100px',
                                    padding: '8px 12px',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    textAlign: 'right'
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {sections.map(({ category, meta, permissions }) => (
                <div key={category} className="permissions-category">
                    <h2 className="category-title">{meta.title}</h2>
                    {meta.description && (
                        <p className="category-description">{meta.description}</p>
                    )}
                    <div className="permissions-grid">
                        {permissions.map((perm) => (
                            <div key={perm.key} className="permission-card">
                                <div className="permission-info">
                                    <label className="permission-label">
                                        {perm.label}
                                    </label>
                                    <p className="permission-description">
                                        {perm.description}
                                    </p>
                                    {perm.modules && perm.modules.length > 0 && (
                                        <div className="permission-modules">
                                            {perm.modules.map((module) => (
                                                <span key={module} className="permission-chip">{module}</span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <label className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={settings[perm.key]}
                                        onChange={() => handleToggle(perm.key)}
                                    />
                                    <span className="toggle-slider"></span>
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            <div className="permissions-footer">
                <button
                    className="btn-save btn-save-large"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Salvataggio in corso...' : '💾 Salva Tutte le Modifiche'}
                </button>
            </div>
        </div>
    );
};

export default StaffPermissions;
